/**
 * @license
 * Copyright (c) 2019 Jonathan Catmull.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NON INFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import type { PipeTransform } from '@angular/core';
import { ChangeDetectorRef, inject, Pipe } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap } from 'rxjs';

import { I18nPlatformService } from '../../../../features/i18n/i18n.service';

export type Unit = 'bytes' | 'KB' | 'MB' | 'GB' | 'TB' | 'PB';
export type UnitPrecisionMap = Record<Unit, number>;

export const defaultPrecisionMap: UnitPrecisionMap = {
  bytes: 0,
  KB: 0,
  MB: 1,
  GB: 1,
  TB: 2,
  PB: 2,
};

/**
 * Convert bytes into largest possible unit.
 * Takes an precision argument that can be a number or a map for each unit.
 * Usage:
 *   bytes | filesize: precision
 * @example
 * // returns 1 KB
 * {{ 1500 | filesize }}
 * @example
 * // returns 2.1 GB
 * {{ 2100000000 | filesize }}
 * @example
 * // returns 1.46 KB
 * {{ 1500 | filesize: 2 }}
 */
@Pipe({
  name: 'filesize',
  pure: false,
})
export class FileSizePipe implements PipeTransform {
  readonly #i18n = inject(I18nPlatformService);
  readonly #ref = inject(ChangeDetectorRef);

  readonly #units: Unit[] = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];

  constructor() {
    this.#i18n.onLangChange$
      .pipe(
        tap(() => this.#ref.markForCheck()),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  /** Converts a byte count to a human-readable size string.
   * @param bytes The number of bytes to format.
   * @param precision A fixed decimal count or a per-unit precision map. Defaults to `defaultPrecisionMap`.
   * @param translate When `true` (default), the result is passed through the i18n service for localisation.
   */
  transform(
    bytes = 0,
    precision: number | UnitPrecisionMap = defaultPrecisionMap,
    translate = true,
  ): string {
    if (isNaN(parseFloat(String(bytes))) || !isFinite(bytes)) {
      return '?';
    }

    let unitIndex = 0;

    while (bytes >= 1024 && this.#units[unitIndex]) {
      bytes /= 1024;
      unitIndex++;
    }

    const unit: Unit = this.#units[unitIndex] || this.#units[this.#units.length - 1];
    const value: string =
      typeof precision === 'number' ? bytes.toFixed(+precision) : bytes.toFixed(precision[unit]);

    if (translate) {
      return this.#i18n.t(`unit.${unit}`, { value });
    }

    return `${value} ${unit}`;
  }
}
